import { render } from "preact";
import { App, MAIN_APP_RELOAD_KEY } from "./App";
import { DeleteStoryPage } from "./microsite/delete/DeleteStoryPage";
import { collectDeviceContext } from "./utils/deviceContext";

// NOTE: main.app (Babylon engine + the @webcore WebXR graph) is intentionally
// NOT imported here. It's a heavy module graph and importing it statically
// pulled it into the entry chunk, delaying the microsite's first paint (and the
// EnterSplash cover image) behind a Babylon parse. App.tsx dynamically imports
// it from a post-mount effect, so the `app` singleton is still constructed —
// just after the first paint instead of blocking it.

// Sentry error logging — staging + production builds (skipped on the dev
// server). Events are tagged with import.meta.env.MODE ('staging' |
// 'production') so the one Sentry project can tell the two builds apart.
if (!import.meta.env.DEV && import.meta.env.VITE_SENTRY_DSN) {
  import("@sentry/browser").then((Sentry) => {
    Sentry.init({
      dsn: import.meta.env.VITE_SENTRY_DSN as string,
      environment: import.meta.env.MODE,
      sendDefaultPii: false, // never attach PII — sensitive content
      integrations: [Sentry.browserTracingIntegration()],
      tracesSampleRate: 0.1,
      // Babylon's WebGL context-restore rebuild can throw asynchronously on
      // iOS after the tab is backgrounded / under GPU memory pressure (a sparse
      // internal attribute-pointer array at ~1000-instance scale — see
      // MURMURATIONS-5). The brief blank canvas the user might see is caused by
      // the context LOSS itself, not this throw, which Babylon swallows and
      // recovers from on the next frame/interaction. Drop the unactionable,
      // user-invisible report. (Recovery is hardened via onContextRestored in
      // DigitalTwinView.tsx and main.app.ts.)
      // Drop unactionable / non-app noise so real regressions stay visible.
      // NOTE: this only suppresses Sentry *reporting* — it changes no runtime
      // behaviour. Draco/importScripts and dynamic-import/chunk-load errors are
      // deliberately NOT filtered here: they are the canary for a broken decoder
      // or a stale deploy and must stay visible.
      ignoreErrors: [
        /_currentBufferPointers/,
        // iOS/WebKit IndexedDB teardown when a tab is backgrounded or storage is
        // reclaimed. Firebase Auth/Performance hold IDB connections; these fire on
        // teardown, not from a user action (MURMURATIONS-2/6/7/9/G/J/N).
        /The database connection is closing/,
        /Connection to Indexed Database server lost/,
        /Database deleted by request of the user/,
        /Attempt to delete range from database without an in-progress transaction/,
        // Fetch/operation aborted on navigation or teardown — benign (MURMURATIONS-6).
        /AbortError/,
        // Injected by the user's browser extension, not our code (MURMURATIONS-P).
        /runtime\.sendMessage/,
        /Tab not found/,
        /Extension context invalidated/,
        /chrome-extension:\/\//,
        // WebKit's own JS<->native bridge error inside 3rd-party iOS in-app
        // browsers (e.g. DuckDuckGo) — not our native bridge (MURMURATIONS-H).
        /WKWebView API client did not respond/,
      ],
    });
    // Fingerprint the device/GPU on every event. The Pixel-9 AR boot hang is
    // invisible partly because a captured event can't be told apart from a
    // working Pixel 7 (same Chrome major, same config) — the unmasked GPU
    // renderer + parsed Chrome/Android version are the real discriminators.
    try {
      const ctx = collectDeviceContext();
      if (ctx.gpuRenderer) Sentry.setTag("gpu_renderer", ctx.gpuRenderer);
      if (ctx.chromeMajor) Sentry.setTag("chrome_major", String(ctx.chromeMajor));
      if (ctx.androidVersion) Sentry.setTag("android_version", ctx.androidVersion);
      Sentry.setContext("device_gpu", ctx as Record<string, unknown>);
    } catch {
      /* never block init */
    }
  });
}

// Google Analytics (gtag.js) is loaded directly from index.html with a
// hard-coded measurement id, gated to the production host. See index.html.

// Eruda dev console — dev only, stripped from production bundle
if (import.meta.env.DEV) {
  import("./utils/eruda").then(({ initEruda }) => initEruda());
}

// Global self-heal for stale lazy-chunk fetches after a redeploy (MURMURATIONS-F).
// Vite fires `vite:preloadError` on the window when a dynamically-imported chunk
// 404s — almost always a browser-cached index.html pointing at a hashed chunk a
// later manual deploy deleted (the AR app is deployed by hand, often). App.tsx's
// loadMainApp already self-heals the AR-launch path; this covers EVERY lazy path
// (the microsite digital twin, the AROverlay graph, the prefetch) so a user on
// any surface recovers onto fresh HTML instead of a degraded/blank view. We reuse
// App.tsx's sessionStorage sentinel so the two paths share one reload budget and
// can't reload-loop; default to "already reloaded" if storage is unreadable
// (private mode / sandboxed webview). preventDefault() stops Vite rethrowing the
// failure as an unhandled rejection on the path it can see.
window.addEventListener("vite:preloadError", (event) => {
  let reloadedAlready = true;
  try {
    reloadedAlready = sessionStorage.getItem(MAIN_APP_RELOAD_KEY) === "1";
  } catch {
    reloadedAlready = true;
  }
  if (reloadedAlready) return; // let loadMainApp surface ArErrorScreen instead of looping
  event.preventDefault();
  try {
    sessionStorage.setItem(MAIN_APP_RELOAD_KEY, "1");
  } catch {
    /* ignore — worst case the chunk error surfaces normally */
  }
  window.location.reload();
});

const appElement = document.getElementById("app");
if (appElement) {
  const isDeleteStoryRoute = window.location.pathname === "/deletestory";
  render(isDeleteStoryRoute ? <DeleteStoryPage /> : <App />, appElement);
}
